import type { ComponentType } from "react";
import { lazy, Suspense, useEffect, useState } from "react";
import styled from "@emotion/styled";
import type _LottieImport from "lottie-react";
import { breakpoints, borderRadius } from "@atlas-int/tokens";

const ANIMATION_URL = "/assets/animations/edited-box.json";

// Lazy: avoids pulling lottie-web (~600 KB) into the landing chunk; only
// loaded when the header animation actually renders. Vite dev sometimes
// double-wraps the CJS default export, so unwrap until we hit a function.
const Lottie = lazy(() =>
  import("lottie-react").then((mod) => {
    let resolved: any = mod;
    while (resolved && typeof resolved !== "function" && resolved.default) {
      resolved = resolved.default;
    }
    return { default: resolved as typeof _LottieImport };
  }),
) as ComponentType<React.ComponentProps<typeof _LottieImport>>;

const AnimationWrapper = styled.div`
  width: 400px;
  height: 395px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.lg}) {
    width: 340px;
    height: 336px;
  }

  @media (max-width: ${breakpoints.md}) {
    width: 280px;
    height: 277px;
  }

  @media (max-width: ${breakpoints.headerMobile}) {
    width: 180px;
    height: 178px;
  }

  @media (max-width: ${breakpoints.xxs}) {
    width: 140px;
    height: 138px;
  }
`;

const PlaceholderCircle = styled.div`
  width: 70%;
  aspect-ratio: 1 / 1;
  background: rgba(255, 255, 255, 0.06);
  border-radius: ${borderRadius.full};
`;

const HeaderAnimation = () => {
  const [animationData, setAnimationData] = useState<object | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(ANIMATION_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setAnimationData(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!animationData) {
    return (
      <AnimationWrapper>
        <PlaceholderCircle />
      </AnimationWrapper>
    );
  }

  return (
    <AnimationWrapper>
      <Suspense fallback={null}>
        <Lottie
          animationData={animationData}
          loop={true}
          autoplay={true}
          style={{ width: "100%", height: "100%" }}
        />
      </Suspense>
    </AnimationWrapper>
  );
};

export default HeaderAnimation;
